import { memo, useState, useCallback, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { KstDataGrid } from '@/components/KstDataGrid';
import { api } from '@/services/api-class';
import { getCurrentCompanyId } from '@/utils/auth';
import moment from 'moment';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const notesAuditLogEntryTemplate = _wg('notesAuditLogEntryTemplate');

function getAuditLogGridColumns(): KstDataGridColumn[] {
  return [
    { field: 'displayId', name: 'ID', size: '50px' },
    {
      field: 'dateCreated',
      name: 'Date/Time',
      template: (e: any) =>
        e.dateCreated
          ? moment(e.dateCreated).format('MM/DD/YYYY HH:mm')
          : '',
    },
    { field: 'loggedByFirstName', name: 'First' },
    { field: 'loggedByLastName', name: 'Last' },
    {
      field: 'kerktonRole',
      name: 'Role',
      template: (e: any) => e.kerktonRole || e.role || '',
    },
    { field: 'action', name: 'Action' },
    {
      field: 'entry',
      name: 'Entry',
      template: (e: any) =>
        e.entry
          ? notesAuditLogEntryTemplate(e.entry).replace(/\n/g, '<br>')
          : '',
    },
  ];
}

export interface NotesAuditLogTabHandle {
  refresh: () => void;
}

interface NotesAuditLogTabProps {
  isVisible: boolean;
}

export const NotesAuditLogTab = memo(forwardRef<NotesAuditLogTabHandle, NotesAuditLogTabProps>(function NotesAuditLogTab({
  isVisible,
}: NotesAuditLogTabProps, ref) {
  const [loading, setLoading] = useState(false);
  const [gridRows, setGridRows] = useState<KstDataGridRows>({ entries: null });
  const [gridPager, setGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    isVisible: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(1);
  const hasLoadedRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const columns = useMemo(() => getAuditLogGridColumns(), []);

  const loadAuditLog = useCallback((pageOverride?: number) => {
    setLoading(true);
    const page =
      pageOverride !== undefined ? pageOverride : currentPageRef.current;

    const operation = {
      data: {
        page: page,
        take: pageSize,
        skip: (page - 1) * pageSize,
      },
    };

    api.getNotesAuditLogEntries(
      getCurrentCompanyId(),
      page,
      String(pageSize),
      JSON.stringify(operation),
      (result: any) => {
        const newTotalCount = result.records || 0;
        const newTotalPages = Math.ceil(newTotalCount / pageSize) || 1;
        const entries = result.entries || [];

        setTotalPages(newTotalPages);
        setGridRows({ entries });
        setGridPager({
          curPage: page,
          numPages: newTotalPages,
          records: newTotalCount,
          isVisible: newTotalPages > 1,
        });
        setLoading(false);
      }
    );
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => loadAuditLog(),
  }), [loadAuditLog]);

  // Load on first visibility
  useEffect(() => {
    if (isVisible && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAuditLog();
    }
  }, [isVisible, loadAuditLog]);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
      loadAuditLog(page);
    },
    [totalPages, loadAuditLog]
  );

  return (
    <div style={{ display: isVisible ? undefined : 'none' }}>
      <div className="kst-results-panel">
        <div className="kst-results-body">
          <KstDataGrid
            columns={columns}
            rows={gridRows}
            pager={gridPager}
            showcheckbox={false}
            showheader={false}
            mobiletitlefield="action"
            loading={loading}
            onGoToPage={goToPage}
          />
        </div>
      </div>
    </div>
  );
}));
